import { createSlice } from "@reduxjs/toolkit";
import Api from "../../api/Api";
import {IObjectKeys} from "../../constants/interfaces";

type State = {
	data: IObjectKeys,
	isLoading: boolean,
	isUpdating: number,
	error: Object | null
}

// Slice
const slice = createSlice({
	name: "storylist",
	initialState: {
		data: {},
		isLoading: false,
		isUpdating: -1, // represents cellId, so we know which cell is updating
		error: null
	},
	reducers: {
		fetchStoryListStart: (state: State) => {
			state.isLoading = true;
		},
		updateStoryListBySortStart: (state : State, action) => {
			state.isUpdating = action.payload;
		},
		fetchStoryListSuccess: (state: State, action) => {
			state.isLoading = false;
			state.isUpdating = -1;
			if (action.payload && action.payload.data) {
				state.data[action.payload.cellId] = action.payload.data.map((item: any) => {
					const result: any = {
						brandId: item.brandId,
						brandName: item.brandName,
						relationId: item.relationId,
						totalPageviews: item.total,
						webPageviews: item.totalWeb,
						appPageviews: item.totalApp,
						totalVisitors: item.totalVisitors,
						webVisitors: item.webVisitors,
						appVisitors: item.appVisitors,
						pubdate: item.timestamp ? Date.parse(item.timestamp) : "",
						title: item.title,
						uri: item.uri,
						facebookConsumptions: item.facebook_consumptions,
						facebookComments: item.facebook_comments,
						facebookLikes: item.facebook_likes,
						facebookShares: item.facebook_shares,
						facebookLinkClicks: item.facebook_link_clicks,
						facebookSocialScore: item.facebook_social_score,
						twitterLikes: item.twitter_likes,
						twitterReply: item.twitter_reply,
						twitterScore: item.twitter_score,
						socialActions: item.social_actions,
						relativeChannels: item.channels,
						section: item.section,
						trend: item.trend,
						isPaid: item.isPaid,
						user_needs: item.user_needs,
						conversion: item.conversion,
						averageReadingTime: item.readingTimeAverage,
						totalReadingTime: item.readingTimeTotal,
						averageReadDepth: item.readDepthAverage,
						averagePageDepth: item.averagePageDepth,
						averageScrollDepth: item.scrollDepthAverage,
						androidPageviews: item.androidPageviews,
						iosPageviews: item.iosPageviews,
						windowsPageviews: item.windowsPageviews,
						linkedinSocial: item.linkedinSocial,
						instagramSocial: item.instagramSocial,
						notifications: item.notification_total,
						secondaryPosts: item.secondary_posts,
						type: item.type
					};

					Object.keys(item).forEach(itemField => {
						if (action.payload.eventFields.indexOf(itemField) !== -1 || action.payload.customFields.indexOf(itemField) !== -1) {
							result[itemField] = item[itemField];
						}
					});

					return result;
				});
			}
		},
		fetchStoryListFailed: (state: State, action) => {
			state.isLoading = false;
			state.isUpdating = -1;
			state.error = action.payload;
		},
		resetToInitialState: (state: State) => {
			state.data = {};
			state.isLoading = false;
			state.error = null;
		}
	},
});

export default slice.reducer;

// Actions
const { 
	fetchStoryListStart, 
	updateStoryListBySortStart,
	fetchStoryListSuccess, 
	fetchStoryListFailed,
	resetToInitialState 
} = slice.actions;

export const getStoryList = (brands: string, fields: Array<string>, interval: any, showNotifications: boolean, notOnHomepage: boolean, sections: string, tags: string, conversions: string, eventFields: string, order: string, weightedAverage: boolean, isPaid: string, videoFilter: string, pageType: string, referrers: string, referrerTypes: string, includeChannels: string, excludeChannels: string, pubdateInterval: any, userNeeds: string, customFields: Array<string>, cellId: number) => async (dispatch: Function) => {
	try {
		dispatch(fetchStoryListStart());
		const api = new Api();
		const result = await api.getStoryList(brands, fields, interval, showNotifications, notOnHomepage, sections, tags, conversions, eventFields, order, weightedAverage, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, pubdateInterval, userNeeds);
		const payload = {
			data: result,
			eventFields: eventFields,
			customFields: customFields,
			cellId
		}
		if (result !== "No internet connection") {
			dispatch(fetchStoryListSuccess(payload));
		}
	} catch (err: any) {
		dispatch(fetchStoryListFailed(err));
	}
};


export const updateStoryListBySort = (brands: string, fields: Array<string>, interval: any, showNotifications: boolean, notOnHomepage: boolean, sections: string, tags: string, conversions: string, eventFields: string, order: string, weightedAverage: boolean, isPaid: string, videoFilter: string, pageType: string, referrers: string, referrerTypes: string, includeChannels: string, excludeChannels: string, pubdateInterval: any, userNeeds: string, customFields: Array<string>, cellId: number) => async (dispatch: Function) => {
	try {
		dispatch(updateStoryListBySortStart(cellId));
		const api = new Api();
		const result = await api.getStoryList(brands, fields, interval, showNotifications, notOnHomepage, sections, tags, conversions, eventFields, order, weightedAverage, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, pubdateInterval, userNeeds);
		const payload = {
			data: result,
			eventFields: eventFields,
			customFields: customFields,
			cellId
		}
		dispatch(fetchStoryListSuccess(payload));
	} catch (err: any) {
		dispatch(fetchStoryListFailed(err));
	}
};

export const resetStoryListState = () => async (dispatch: Function) => {
	dispatch(resetToInitialState())
}
